

///////////////////////////////
// validate incoming JWT token
///////////////////////////////

export function validateAccessToken(accessToken: string): string {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new Error('Valid access token is required');
  }
  
  const trimmed = accessToken.trim();
  
  // Basic checks
  if (trimmed.length === 0 || trimmed.length > 2048) {
    throw new Error('Invalid token length');
  }
  
  // JWT structure check
  const parts = trimmed.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid token format');
  }
  
  // Basic expiry check
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      throw new Error('Token expired');
    }
  } catch (e) {
    // If we can't decode, let backend handle it
    if (e instanceof Error && e.message.includes('expired')) {
      throw e;
    }
  }
  
  return trimmed;
}



///////////////////////////////////////////
// validate ethereum based token addresses
///////////////////////////////////////////

export function validateTokenAddresses(input: string | string[]): string[] {
  // Convert single string to array
  const addresses = Array.isArray(input) ? input : [input];
  
  if (addresses.length === 0) {
    throw new Error('Token addresses cannot be empty');
  }
  
  const sanitizedAddresses = addresses
    .filter(address => typeof address === 'string' && address.trim().length > 0)
    .map(address => address.trim())
    .filter(address => /^0x[a-fA-F0-9]{40}$/.test(address));
  
  if (sanitizedAddresses.length === 0) {
    throw new Error('No valid token addresses provided');
  }
  
  return sanitizedAddresses;
}




///////////////////////////////////
// validate ethereum based wallets 
///////////////////////////////////

export function validateWallets(wallets: string[], maxCount: number = 10): string[] {
 if (!Array.isArray(wallets) || wallets.length === 0) {
   throw new Error('Wallets array is required and cannot be empty');
 }
 
 if (wallets.length > maxCount) {
   throw new Error(`Maximum ${maxCount} wallets allowed`);
 }
 
 const sanitizedWallets = wallets
   .filter(wallet => typeof wallet === 'string' && wallet.trim().length > 0)
   .map(wallet => {
     const trimmed = wallet.trim();
     if (trimmed.length > 255) {
       throw new Error('Wallet address too long');
     }
     return trimmed;
   })
  // Filter that accepts ethereum and ENS based addresses
  .filter(address => 
    /^0x[a-fA-F0-9]{40}$/.test(address) || 
    /^[a-z0-9]+(-[a-z0-9]+)*\.eth$/.test(address.toLowerCase())
  );
 
 if (sanitizedWallets.length === 0) {
   throw new Error('No valid wallet addresses provided');
 }
 
 return sanitizedWallets;
}




///////////////////////////////////
// validate allowed chains 
///////////////////////////////////

export function validateChains(input: string | string[]): string[] {
  const chains = Array.isArray(input) ? input : [input];
  const ALLOWED_CHAINS = ['eth', 'bsc', 'avalanche', 'avax', 'base'];
  
  if (chains.length === 0) {
    throw new Error('Chains cannot be empty');
  }
  
  const validChains = chains.filter(chain => 
    typeof chain === 'string' && 
    chain.trim().length > 0 &&
    ALLOWED_CHAINS.includes(chain.trim())
  );
  
  if (validChains.length === 0) {
    throw new Error('No valid chains provided');
  }
  
  return validChains.map(chain => chain.trim());
}